import React, { useState } from 'react';
import { User, Lock, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import ModalShell, { ModalHeader, ModalBody, ModalFooter } from './ModalShell';

interface Props {
  onClose: () => void;
}

const UserProfileModal: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [tab, setTab] = useState<'profile' | 'password'>('profile');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const clearMessages = () => { setError(''); setSuccess(''); };

  const handleSaveProfile = async () => {
    clearMessages();
    if (!firstName.trim() || !lastName.trim()) {
      setError(t('profile.nameRequired', 'First and last name are required'));
      return;
    }
    setSaving(true);
    try {
      const res = await apiService.put('/api/auth/profile', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      if (res.success) setSuccess(t('profile.saveSuccess', 'Profile updated successfully'));
      else setError(res.message || t('common.error', 'Error'));
    } catch {
      setError(t('common.error', 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async () => {
    clearMessages();
    if (!newPassword || !confirmPassword) {
      setError(t('profile.passwordRequired', 'Please enter and confirm your new password'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('profile.passwordMismatch', 'Passwords do not match'));
      return;
    }
    if (newPassword.length < 6) {
      setError(t('profile.passwordTooShort', 'Password must be at least 6 characters'));
      return;
    }
    setSaving(true);
    try {
      const res = await apiService.post('/api/auth/change-password', { newPassword });
      if (res.success) {
        setSuccess(t('profile.passwordChanged', 'Password updated successfully'));
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(res.message || t('common.error', 'Error'));
      }
    } catch {
      setError(t('common.error', 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const eyeBtn: React.CSSProperties = {
    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--ink-muted)', display: 'flex', padding: 0,
  };

  return (
    <ModalShell isOpen onClose={onClose} size="sm">
      <ModalHeader title={t('profile.title', 'My Profile')} onClose={onClose} />

      <div style={{ display: 'flex', borderBottom: '1px solid var(--line)', padding: '0 28px' }}>
        {(['profile', 'password'] as const).map(k => (
          <button
            key={k}
            onClick={() => { setTab(k); clearMessages(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 4px', marginRight: 20,
              fontSize: 13, fontWeight: 500,
              background: 'none', border: 'none', cursor: 'pointer',
              color: tab === k ? 'var(--ink)' : 'var(--ink-muted)',
              borderBottom: tab === k ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {k === 'profile' ? <User size={13} /> : <Lock size={13} />}
            {k === 'profile' ? t('profile.tab.profile', 'Profile') : t('profile.tab.password', 'Password')}
          </button>
        ))}
      </div>

      <ModalBody single>
        {error && (
            <div style={{
              padding: '8px 12px', borderRadius: 'var(--radius)',
              background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
              color: 'var(--danger)', fontSize: 13, marginBottom: 16,
            }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{
              padding: '8px 12px', borderRadius: 'var(--radius)',
              background: 'color-mix(in srgb, #22c55e 10%, transparent)',
              color: '#16a34a', fontSize: 13, marginBottom: 16,
            }}>
              {success}
            </div>
          )}

          {tab === 'profile' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="field">
                  <label className="field-label">{t('auth.firstName', 'First Name')}</label>
                  <input value={firstName} onChange={e => setFirstName(e.target.value)} />
                </div>
                <div className="field">
                  <label className="field-label">{t('auth.lastName', 'Last Name')}</label>
                  <input value={lastName} onChange={e => setLastName(e.target.value)} />
                </div>
              </div>
              <div className="field">
                <label className="field-label">{t('auth.email', 'Email')}</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">{t('auth.phone', 'Phone')}</label>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
              </div>
            </>
          )}

          {tab === 'password' && (
            <>
              <div className="field">
                <label className="field-label">{t('profile.newPassword', 'New Password')}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    style={{ paddingRight: 38 }}
                  />
                  <button type="button" onClick={() => setShowNew(v => !v)} style={eyeBtn}>
                    {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div className="field">
                <label className="field-label">{t('profile.confirmPassword', 'Confirm Password')}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    style={{ paddingRight: 38 }}
                  />
                  <button type="button" onClick={() => setShowConfirm(v => !v)} style={eyeBtn}>
                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
            </>
          )}
      </ModalBody>

      <ModalFooter>
        <button className="btn-s ghost" onClick={onClose}>{t('common.cancel', 'Cancel')}</button>
        <button
          className="btn-s primary"
          onClick={tab === 'profile' ? handleSaveProfile : handleSetPassword}
          disabled={saving}
        >
          {saving
            ? t('common.saving', 'Saving…')
            : tab === 'profile'
              ? t('common.save', 'Save')
              : t('profile.setPassword', 'Set Password')}
        </button>
      </ModalFooter>
    </ModalShell>
  );
};

export default UserProfileModal;
